import { Plus } from "lucide-react";
import { CanAccess, useGetList, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type { DealActivityResult, DealActivityType } from "../types";
import {
  countActiveResults,
  orderActivityTypesForList,
} from "./activityTypeForm";

/**
 * Almikia Epic EA (TASK-113): "Actividades" -- direccion-only (mirrors
 * `DealStageConfigList`'s `CanAccess` gate: every role reads the RLS-backed
 * catalogue, only direccion may reach this screen at all). Each row shows
 * the activity's name, its number of active results and its attempt cap
 * (when set), active activities first, then archived ones (each group by
 * `(position, id)`, DATA CONTRACT EA (1)). No reorder UI here (the ticket
 * ships none for EA).
 */
export const DealActivityTypeList = () => (
  <CanAccess
    resource="deal_activity_types"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <DealActivityTypeListContent />
  </CanAccess>
);

const DealActivityTypeListContent = () => {
  const translate = useTranslate();

  const {
    data: activityTypes,
    isPending: activityTypesPending,
    error: activityTypesError,
    refetch: refetchActivityTypes,
  } = useGetList<DealActivityType>("deal_activity_types", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });
  const { data: results, isPending: resultsPending } =
    useGetList<DealActivityResult>("deal_activity_results", {
      pagination: { page: 1, perPage: 500 },
    });

  if (activityTypesError) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetchActivityTypes()}
      />
    );
  }
  if (activityTypesPending || resultsPending || !activityTypes || !results) {
    return <ListCardsSkeleton rows={6} />;
  }

  const ordered = orderActivityTypesForList(activityTypes);

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between px-1">
        <h1 className="text-xl font-semibold">
          {translate("resources.deal_activity_types.title")}
        </h1>
        <Button asChild size="sm" className="min-h-11">
          <Link to="/deal_activity_types/create">
            <Plus className="size-4 mr-1" />
            {translate("resources.deal_activity_types.action.new_activity")}
          </Link>
        </Button>
      </div>

      {ordered.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            {translate("resources.deal_activity_types.empty")}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {ordered.map((activity) => {
              const activeResultsCount = countActiveResults(
                activity.id,
                results,
              );
              const isArchived = activity.archived_at != null;
              return (
                <Link
                  key={activity.id}
                  to={`/deal_activity_types/${activity.id}`}
                  className="flex items-center gap-3 p-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium truncate">
                        {activity.name}
                      </span>
                      {isArchived ? (
                        <StatusPill
                          tone="not-ok"
                          text={translate(
                            "resources.deal_activity_types.archived_marker",
                          )}
                        />
                      ) : null}
                    </div>
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {translate(
                        "resources.deal_activity_types.results_count",
                        { smart_count: activeResultsCount },
                      )}
                      {activity.max_attempts != null
                        ? ` · ${translate(
                            "resources.deal_activity_types.cap_label",
                            { max: activity.max_attempts },
                          )}`
                        : ""}
                    </p>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
